import type { SprayCalibration } from '../services/sprayService';

/**
 * Default number of days a calibration is considered valid for. 12 months
 * (~365 days) per the spray-certificate domain rule. Can be overridden
 * per-tenant in the future (Tenant Settings).
 */
export const DEFAULT_CALIBRATION_THRESHOLD_DAYS = 365;

export type CalibrationStatus = 'green' | 'amber' | 'red';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Return the validity status of a machine's latest calibration against a
 * configurable day threshold.
 *
 * Rules:
 *   - `red`    — no calibrations, OR the latest calibration is older than the
 *                threshold (expired).
 *   - `amber`  — the latest calibration is older than 75% of the threshold
 *                but still within the threshold (expiring soon).
 *   - `green`  — the latest calibration is within the first 75% of the
 *                threshold window (fresh).
 *
 * @param calibrations The machine's calibrations array (may be undefined or
 *                     empty).
 * @param thresholdDays How many days a calibration remains valid.
 */
export function getCalibrationStatus(
  calibrations: SprayCalibration[] | undefined,
  thresholdDays: number
): CalibrationStatus {
  if (!calibrations || calibrations.length === 0) return 'red';

  // Soft-deleted calibrations are excluded from the age calculation. Track
  // machine_calibration_overrides_20260427 / Phase 5.
  const active = calibrations.filter(c => c.status !== 'deleted');
  if (active.length === 0) return 'red';

  // Find the most recent active calibration by date.
  const latest = [...active].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  )[0];

  const ageDays = (Date.now() - new Date(latest.date).getTime()) / DAY_MS;

  if (ageDays > thresholdDays) return 'red';
  if (ageDays > thresholdDays * 0.75) return 'amber';
  return 'green';
}
